import React, { useEffect, useMemo, useState } from 'react';
import { batch, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { ContinueStripeLink, GetStripeLink, StripeStatus } from '../../../connection/integrations';
import { getGoogleAnalytics, getStripeStatus, setGoogleAnalytics, setStripeContact } from '../../../store/actions/admin/integrations';
import { useTypedSelector } from '../../../store/reducers/use-typed-selector';
import ModalComponent from '../../general-ui/modal/modal';
import WaitingIndicator from '../../general-ui/waiting-indicator/waiting-indicator';
import './settings-integrations.scss';
import TextInput, { Validation } from '../../general-ui/text-input/text';
import { isValidEmail } from '../../../utils/utils';
import usePrevious from '../../../utils/use-previous';
import { useCallback } from 'react';

//Variation from design - looked up current logos for these partners as of 2021
//Designs contain old logos
enum EIntegrationLogos {
	MicrosoftDynamics = 'https://assets.brandnot.live/assets/Microsoft_Dynamics_Logo.svg',
	Addthis = 'https://assets.brandnot.live/assets/addthis-icon.svg',
	Hubspot = 'https://assets.brandnot.live/assets/hubspot-1.svg',
	GoogleAnalytics = 'https://assets.brandnot.live/assets/ic_analytics.svg',
	Salesforce = 'https://assets.brandnot.live/assets/salesforce-2.svg',
	Saml = 'https://assets.brandnot.live/assets/saml-logo.svg',
	Stripe = 'https://assets.brandnot.live/assets/stripe-purple.svg',
}

export default function SettingsIntegrations({ overview }: { overview?: true; }): JSX.Element {
	const {
		IntegrationsReducer: {
			stripe,
			// FUTURE DEV: keeping for when we wire up google analytics
			// googleAnalytics
		},
		AuthReducer: {
			token, user
		}
	} = useTypedSelector(state => state);

	const dispatch = useDispatch();

	const userPermission = useMemo(() => {
		return user?.channels[user?.active_channel].some(permission => permission === "owner" || permission === "admin");
	}, [user]);

	const [setupStripe, setSetupStripe] = useState(false);
	const [setupGoogleAnalytics, setSetupGoogleAnalytics] = useState(false);

	async function handleStripe() {
		switch (stripe.status) {
			case StripeStatus.none: {
				//these are done in-line rather than in redux because they have no affect on any state outside this component
				//they simply get an account link from Stripe and we do an immediate redirect
				const stripeLinks = await GetStripeLink(token!);
				window.location.href = stripeLinks.url;
				return;
			}
			case StripeStatus.incomplete: {
				//these are done in-line rather than in redux because they have no affect on any state outside this component
				//they simply get an account link from Stripe and we do an immediate redirect
				const stripeLinks = await ContinueStripeLink(token!);
				window.location.href = stripeLinks.url;
				return;
			}
			case StripeStatus.ready: {
				setSetupStripe(true);
			}
		}
	}

	useEffect(() => {
		batch(() => {
			dispatch(getStripeStatus(token!));
			dispatch(getGoogleAnalytics(token!, user!.active_channel));
		});
	}, [dispatch, token, user, user?.active_channel]);

	//returning two things from the memo as an array - first item is the text for the button, second item is the class name for the button
	const [stripeButtonContents, stripeButtonClass] = useMemo(() => {
		if (stripe.loading) {
			return ["Loading...", ''];
		}

		switch (stripe.status) {
			case StripeStatus.none: {
				return ["Configure", "primary"];
			}
			case StripeStatus.incomplete: {
				return ["Continue Setup", "primary"];
			}
			case StripeStatus.ready: {
				return ["Edit", ""];
			}
			default: return ["Configure", "primary"];
		}
	}, [stripe]);

	// FUTURE DEV: keeping for when we wire up google analytics
	// const openGoogleAnalyticsModal = useCallback(() => {
	// 	setSetupGoogleAnalytics(true);
	// }, []);

	return (
		<div className="settings-integrations-container">

			{/* GOOGLE ANALYTICS INTEGRATION - Removing for internal launch */}
			{/* <div className="settings-integration">
				<div className="integration-logo">
					<img src={EIntegrationLogos.GoogleAnalytics} alt="Google Analytics" />
				</div>
				<div className="integration-text">
					<p>Google Analytics</p>
					<span>Track activity with Google Analytics</span>
				</div>
				<div className="integration-actions"> */}
			{/* NOTE: this button is set to primary/configure as an example - this should be the state for a non-configured integration */}
			{/* <button onClick={openGoogleAnalyticsModal} className="small primary" disabled={!userPermission}>{googleAnalytics ? "Edit" : "Configure"}</button>
				</div>
			</div> */}

			{/* ADDTHIS INTEGRATION */}
			{/* <div className="settings-integration">
				<div className="integration-logo">
					<img src={EIntegrationLogos.Addthis} alt="Addthis" />
				</div>
				<div className="integration-text">
					<p>AddThis</p>
					<span>Allow sharing on multiple social platforms</span>
				</div>
				<div className="integration-actions">
					<button className="small" disabled={!userPermission}>Edit</button>
				</div>
			</div> */}

			{/* SALESFORCE INTEGRATION */}
			{/* <div className="settings-integration">
				<div className="integration-logo">
					<img src={EIntegrationLogos.Salesforce} alt="Salesforce" />
				</div>
				<div className="integration-text">
					<p>Salesforce</p>
					<span>Track activity in Salesforce</span>
				</div>
				<div className="integration-actions">
					<button className="small" disabled={!userPermission}>Edit</button>
				</div>
			</div> */}

			{/* HUBSPOT INTEGRATION (not shown on overview tab) */}
			{/* {!overview &&
				<div className="settings-integration">
					<div className="integration-logo">
						<img src={EIntegrationLogos.Hubspot} alt="HubSpot" />
					</div>
					<div className="integration-text">
						<p>HubSpot</p>
						<span>Track activity in HubSpot</span>
					</div>
					<div className="integration-actions">
					<button className="small" disabled={!userPermission}>Edit</button>
				</div>
			</div>
			} */}

			{/* SAML INTEGRATION (not shown on overview tab) */}
			{/* {!overview &&
				<div className="settings-integration">
					<div className="integration-logo">
						<img src={EIntegrationLogos.Saml} alt="SAML 2.0" />
					</div>
					<div className="integration-text">
						<p>SAML 2.0</p>
						<span>Allow login with SAML 2.0</span>
					</div>
					<div className="integration-actions">
					<button className="small" disabled={!userPermission}>Edit</button>
				</div>
			</div>
			} */}

			{/* MICROSOFT DYNAMICS INTEGRATION (not shown on overview tab) */}
			{/* {!overview &&
				<div className="settings-integration">
					<div className="integration-logo">
						<img src={EIntegrationLogos.MicrosoftDynamics} alt="Microsoft Dynamics 365" />
					</div>
					<div className="integration-text">
						<p>Microsoft Dynamics 365</p>
						<span>Track activity with Microsoft Dynamics 365</span>
					</div>
					<div className="integration-actions">
					<button className="small" disabled={!userPermission}>Edit</button>
				</div>
			</div>
			} */}

			{/* STRIPE INTEGRATION (not shown on overview tab) */}
			{!overview && <>
				<div className="settings-integration">
					<div className="integration-logo">
						<img src={EIntegrationLogos.Stripe} alt="Stripe" />
					</div>
					<div className="integration-text">
						<p>Stripe</p>
						<span>Collect payment for event access</span>
					</div>
					<div className="integration-actions">
						{/* NOTE: Stripe has been ported in from events 2.5 */}
						<button
							disabled={stripe.loading || !userPermission}
							onClick={handleStripe}
							className={classNames("small", stripeButtonClass)}
						>
							{stripeButtonContents}
						</button>
					</div>
				</div>
				<StripeModal
					open={setupStripe}
					close={() => setSetupStripe(false)}
				/>
				<GoogleAnalyticsModal
					open={setupGoogleAnalytics}
					close={() => setSetupGoogleAnalytics(false)}
				/>
			</>}
		</div>
	);
}

interface GoogleModalProps {
	open: boolean;
	close: () => void;
}

function GoogleAnalyticsModal({ open, close }: GoogleModalProps) {
	const {
		IntegrationsReducer: {
			googleAnalytics,
			loadingGoogleAnalytics
		},
		AuthReducer: {
			token,
			user
		}
	} = useTypedSelector(state => state);
	const dispatch = useDispatch();
	const [googleKey, setGoogleKey] = useState(googleAnalytics?.google_id ?? "");
	const [error, setError] = useState(false);

	const save = useCallback(() => {
		dispatch(setGoogleAnalytics(token!, user!.active_channel, googleKey));
		close();
	}, [dispatch, close, googleKey, token, user]);

	const update = useCallback(({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
		setGoogleKey(value);

		setError(!value.startsWith('UA-'));
	}, []);

	useEffect(() => {
		setGoogleKey(googleAnalytics?.google_id ?? "");
	}, [googleAnalytics]);

	return (
		<ModalComponent
			open={open}
			cancellable={true}
			closeable={false}
			title={"Google Analytics Integration"}
			onRequestClose={close}
			footer={
				<>
					<button onClick={close}>Cancel</button>
					<button onClick={save} className="primary" disabled={!googleKey || loadingGoogleAnalytics}>{loadingGoogleAnalytics ? <WaitingIndicator /> : "Save"}</button>
				</>
			}
		>
			<div className="google-analytics-modal">
				<p>Please enter your Google Analytics key. Example: "GA-123456-1"</p>
				<br />
				<TextInput
					placeholder="Example: GA-123456-1"
					onChange={update}
					defaultValue={googleAnalytics?.google_id ?? ""}
					label="Google Analytics Key"
					valid={error ? Validation.error : Validation.normal}
				/>
				{error && (
					<p className="error">Please enter a valid Google Analytics key</p>
				)}
			</div>
		</ModalComponent>
	);
}
interface StripeModalProps {
	open: boolean;
	close: () => void;
}

function StripeModal({ open, close }: StripeModalProps) {
	const {
		IntegrationsReducer: {
			stripe
		},
		AuthReducer: {
			token
		}
	} = useTypedSelector(state => state);

	const [email, setEmail] = useState(stripe.contact ?? "");
	const [emailError, setEmailError] = useState(false);
	const wasSetting = usePrevious(stripe.setting_contact);

	const dispatch = useDispatch();

	function handleEmail({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
		setEmail(value);
		setEmailError(!!value && !isValidEmail(value));
	}

	function updateEmail() {
		if (!emailError) {
			dispatch(setStripeContact(token!, email));
		}
	}

	const validationState = useMemo(() => {
		if (emailError) return Validation.error;

		if (email) return Validation.ok;

		return Validation.normal;
	}, [email, emailError]);

	useEffect(() => {
		if (!stripe.setting_contact && wasSetting) {
			close();
		}
	}, [stripe, wasSetting, close]);

	return (
		<ModalComponent
			open={open}
			cancellable={true}
			closeable={false}
			title={"Stripe Integration"}
			onRequestClose={close}
			footer={
				<>
					<button onClick={close}>Cancel</button>
					<button onClick={updateEmail} className="primary" disabled={!email || emailError || stripe.setting_contact}>{stripe.setting_contact ? <WaitingIndicator /> : "Done"}</button>
				</>
			}
		>
			<div className="stripe-modal">
				<TextInput
					defaultValue={stripe.contact ?? ""}
					label={"Stripe Contact Email"}
					placeholder={"Enter a contact email for Stripe"}
					email={true}
					onChange={handleEmail}
					valid={validationState}
				/>
			</div>
		</ModalComponent>
	);
}